import { useTranslation } from 'react-i18next';

import { Button } from 'components/ui/button';
import { USAGE_LIMITS_I18N } from 'features/usage-config/constants/usage-meters';
import { SettlementStrategy } from 'features/usage-config/types/usage-enforcement.model';

import { UsageCardFrame, UsageFieldOrigin } from './usage-card-frame';

const T = USAGE_LIMITS_I18N;

export interface SettlementCardProps {
  settlement: SettlementStrategy;
  onSettlementChange: (settlement: SettlementStrategy) => void;
  dimmed?: boolean;
  readOnly?: boolean;
  settlementInherited?: boolean;
  onFollowSettlement?: () => void;
}

/** Whose subscription pays. The widget and channels follow it too: org-wise the shared
 * plan pays for them, user-wise the person who set them up does. */
export const SettlementCard = ({
  settlement,
  onSettlementChange,
  dimmed,
  readOnly,
  settlementInherited,
  onFollowSettlement,
}: SettlementCardProps) => {
  const { t } = useTranslation();
  const note = settlement === 'user' ? T.SETTLEMENT_USER_NOTE : T.SETTLEMENT_ORG_NOTE;

  return (
    <UsageCardFrame cardId="settlement" label={t(T.SETTLEMENT_TITLE)} dimmed={dimmed}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <h3 className="text-sm font-semibold">{t(T.SETTLEMENT_TITLE)}</h3>
          <p className="mt-1 text-xs text-muted-foreground">{t(T.SETTLEMENT_DESCRIPTION)}</p>
        </div>
        <UsageFieldOrigin field="settlement" inherited={settlementInherited} readOnly={readOnly} />
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label={t(T.SETTLEMENT_TITLE)}>
        {onFollowSettlement && (
          <Button
            type="button"
            size="sm"
            variant={settlementInherited ? 'default' : 'outline'}
            aria-pressed={Boolean(settlementInherited)}
            disabled={readOnly}
            onClick={onFollowSettlement}
          >
            {t(T.FOLLOW_TENANT)}
          </Button>
        )}
        {(['org', 'user'] as SettlementStrategy[]).map((option) => (
          <Button
            key={option}
            type="button"
            size="sm"
            variant={!settlementInherited && settlement === option ? 'default' : 'outline'}
            aria-pressed={!settlementInherited && settlement === option}
            disabled={readOnly}
            onClick={() => onSettlementChange(option)}
          >
            {option === 'org' ? t(T.SETTLEMENT_ORG) : t(T.SETTLEMENT_USER)}
          </Button>
        ))}
      </div>

      <p className="rounded-sm bg-muted px-3 py-2 text-sm text-muted-foreground">{t(note)}</p>
    </UsageCardFrame>
  );
};
